import { generateDarkColor } from '@logto/core-kit';
import { AgreeToTermsPolicy, SignInMode } from '../db-entries/index.js';
import { ForgotPasswordMethod, MfaFactor, MfaPolicy, OrganizationRequiredMfaPolicy, SignInIdentifier, } from '../foundations/index.js';
import { adminTenantId, defaultTenantId } from './tenant.js';
export const defaultPrimaryColor = '#2E4D44';
export const aletynLegalBaseUrl = 'https://accounts.aletyn.com/legal';
export const aletynTermsOfUseUrl = 'https://accounts.aletyn.com/legal/terms';
export const aletynPrivacyPolicyUrl = 'https://accounts.aletyn.com/legal/privacy';
const parseAbsoluteUrl = (value) => {
    try {
        return new URL(value);
    }
    catch {
        throw new Error('Legal base URL must be an absolute HTTP or HTTPS URL');
    }
};
export const normalizeAletynLegalBaseUrl = (value) => {
    if (!value || value.trim() !== value) {
        throw new Error('Legal base URL must be a non-empty URL without surrounding whitespace');
    }
    const url = parseAbsoluteUrl(value);
    if (!['http:', 'https:'].includes(url.protocol)) {
        throw new Error('Legal base URL must use HTTP or HTTPS');
    }
    if ([url.username, url.password].some(Boolean)) {
        throw new Error('Legal base URL must not contain user information');
    }
    // URL.search/hash are empty for a bare trailing `?`/`#`, so inspect href too.
    if (['?', '#'].some((marker) => url.href.includes(marker))) {
        throw new Error('Legal base URL must not contain a query or fragment');
    }
    return `${url.origin}${url.pathname.replace(/\/+$/, '')}`;
};
export const createAletynLegalUrls = (baseUrl = aletynLegalBaseUrl) => {
    const normalizedBaseUrl = normalizeAletynLegalBaseUrl(baseUrl);
    return Object.freeze({
        termsOfUseUrl: `${normalizedBaseUrl}/terms`,
        privacyPolicyUrl: `${normalizedBaseUrl}/privacy`,
    });
};
export const createDefaultSignInExperience = (forTenantId, isCloud, options = {}) => {
    const legalUrls = createAletynLegalUrls(options.legalBaseUrl);
    return Object.freeze({
        tenantId: forTenantId,
        id: 'default',
        color: {
            primaryColor: defaultPrimaryColor,
            isDarkModeEnabled: false,
            darkPrimaryColor: generateDarkColor(defaultPrimaryColor),
        },
        branding: {
            logoUrl: undefined,
            darkLogoUrl: undefined,
        },
        hideLogtoBranding: true,
        languageInfo: {
            autoDetect: true,
            fallbackLanguage: 'en',
        },
        ...legalUrls,
        agreeToTermsPolicy: AgreeToTermsPolicy.Automatic,
        signUp: {
            identifiers: [SignInIdentifier.Email],
            password: true,
            verify: true,
            secondaryIdentifiers: [{ identifier: SignInIdentifier.Username }],
        },
        signIn: {
            methods: [
                {
                    identifier: SignInIdentifier.Email,
                    password: true,
                    verificationCode: false,
                    isPasswordPrimary: true,
                },
            ],
        },
        socialSignInConnectorTargets: [],
        signInMode: SignInMode.SignInAndRegister,
        passkeySignIn: {
            enabled: true,
            showPasskeyButton: true,
            allowAutofill: true,
            allowPasswordRecovery: true,
        },
        // SMS is intentionally not a bootstrap dependency. Phone recovery becomes
        // available only after its connector, templates, and abuse controls are
        // configured in the tenant; email and discoverable Passkey recovery work
        // from the first boot.
        forgotPasswordMethods: [ForgotPasswordMethod.EmailVerificationCode],
        customCss: null,
        customContent: {},
        customUiAssets: null,
        signUpProfileFields: null,
        passwordPolicy: {},
        mfa: isCloud
            ? {
                factors: [],
                policy: MfaPolicy.UserControlled,
            }
            : {
                factors: [MfaFactor.TOTP, MfaFactor.WebAuthn, MfaFactor.BackupCode],
                policy: MfaPolicy.PromptAtSignInAndSignUp,
                organizationRequiredMfaPolicy: OrganizationRequiredMfaPolicy.NoPrompt,
            },
    });
};
/** @deprecated Use `createDefaultSignInExperience()` instead. */
export const defaultSignInExperience = createDefaultSignInExperience(defaultTenantId, false);
export const createAdminTenantSignInExperience = (options = {}) => {
    const legalUrls = createAletynLegalUrls(options.legalBaseUrl);
    return Object.freeze({
        ...defaultSignInExperience,
        ...legalUrls,
        tenantId: adminTenantId,
        color: {
            ...defaultSignInExperience.color,
            isDarkModeEnabled: true,
        },
        signInMode: SignInMode.Register,
        branding: {
            logoUrl: undefined,
            darkLogoUrl: undefined,
        },
        signUp: {
            identifiers: [SignInIdentifier.Username],
            password: true,
            verify: false,
        },
        signIn: {
            methods: [
                {
                    identifier: SignInIdentifier.Username,
                    password: true,
                    verificationCode: false,
                    isPasswordPrimary: true,
                },
            ],
        },
        passwordPolicy: options.disablePwnedPasswordCheck
            ? {
                ...defaultSignInExperience.passwordPolicy,
                rejects: {
                    ...defaultSignInExperience.passwordPolicy?.rejects,
                    pwned: false,
                },
            }
            : defaultSignInExperience.passwordPolicy,
        mfa: {
            factors: [MfaFactor.TOTP, MfaFactor.WebAuthn, MfaFactor.BackupCode],
            policy: MfaPolicy.NoPrompt,
            organizationRequiredMfaPolicy: OrganizationRequiredMfaPolicy.Mandatory,
        },
    });
};
